import { SwipeCard } from './SwipeCard';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus, Zap } from 'lucide-react';

export interface InsightData {
  id: string;
  title: string;
  value: string | number;
  change?: number; // percentage change
  changeLabel?: string;
  description?: string;
  actionLabel?: string;
  actionPayload?: any;
}

interface InsightCardProps {
  insight: InsightData;
  onDismiss: (id: string) => void;
  onAct: (id: string, payload?: any) => void;
  isTopCard?: boolean;
}

export function InsightCard({
  insight,
  onDismiss,
  onAct,
  isTopCard = true,
}: InsightCardProps) {
  const getTrendIcon = () => {
    if (!insight.change) return <Minus className="h-4 w-4" />;
    if (insight.change > 0) return <TrendingUp className="h-4 w-4" />;
    return <TrendingDown className="h-4 w-4" />;
  };

  const getTrendColor = () => {
    if (!insight.change) return 'text-muted-foreground';
    if (insight.change > 0) return 'text-success';
    return 'text-destructive';
  };

  return (
    <SwipeCard
      onSwipeLeft={() => onDismiss(insight.id)}
      onSwipeRight={() => onAct(insight.id, insight.actionPayload)}
      isTopCard={isTopCard}
      leftLabel="Got it"
      rightLabel={insight.actionLabel || 'View More'}
      className="w-full h-full"
    >
      <div className="p-6 h-full flex flex-col items-center justify-center text-center">
        {/* Icon */}
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
          <Zap className="h-8 w-8 text-primary" />
        </div>

        {/* Title */}
        <h3 className="text-lg font-medium text-muted-foreground mb-2">
          {insight.title}
        </h3>

        {/* Value */}
        <div className="text-5xl font-bold text-foreground mb-3">
          {insight.value}
        </div>

        {/* Change indicator */}
        {insight.change !== undefined && (
          <div className={cn('flex items-center gap-1', getTrendColor())}>
            {getTrendIcon()}
            <span className="font-medium">
              {insight.change > 0 ? '+' : ''}{insight.change}%
            </span>
            {insight.changeLabel && (
              <span className="text-muted-foreground ml-1">
                {insight.changeLabel}
              </span>
            )}
          </div>
        )}

        {/* Description */}
        {insight.description && (
          <p className="text-sm text-muted-foreground mt-4 max-w-xs">
            {insight.description}
          </p>
        )}

        {/* Spacer */}
        <div className="flex-1 min-h-8" />

        {/* Action hint */}
        <div className="flex items-center justify-center gap-6 pt-4 border-t border-border/50 w-full">
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="text-lg">←</span>
            <span className="text-sm">Got it</span>
          </div>
          <div className="w-px h-6 bg-border" />
          <div className="flex items-center gap-2 text-primary">
            <span className="text-sm font-medium">
              {insight.actionLabel || 'View Details'}
            </span>
            <span className="text-lg">→</span>
          </div>
        </div>
      </div>
    </SwipeCard>
  );
}
